import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { AddCircleOutline, CloseRounded } from '@mui/icons-material'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { Control, Controller, SubmitHandler, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { useAuth0 } from '@auth0/auth0-react'
import { v4 as uuidv4 } from 'uuid'
import {
  useRequestOrderUploadUrl,
  useSubmitOrderRequest,
} from '../../logic/useApiMutation'
import { OrderRequestSendToOption } from '../../types/portalTypes/PortalDomainOrg'
import { OrderRequestDocument } from '../../types/orderRequest'

const ORDER_TYPES = ['Purchase', 'Refinance', 'Equity Loan / HELOC', 'Reverse Mortgage', 'Construction', 'Commercial']
const AGENT_ROLES = ["Buyer's Agent", "Seller's Agent", 'Attorney', 'Lender', 'Other']
const PROPERTY_TYPES = ['Single Family', '1-4 Family', 'PUD', 'Condo', 'Cooperative', 'Unimproved', 'Vacant Land', 'Other']
const FINANCING_TYPES = ['Conventional', 'FHA', 'VA']
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY']
const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'pdf']
const ACCEPT = ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join()

const EMAIL_PATTERN = {
  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  message: 'Please enter a valid email address',
}

const PHONE_PATTERN = {
  value: /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/,
  message: 'Please enter a valid phone number',
}

interface PersonFields {
  FirstName: string
  MiddleName: string
  LastName: string
  Email: string
  Phone: string
  ContactSameAsProperty: boolean
  ContactAddress1: string
  ContactAddress2: string
  ContactCity: string
  ContactState: string
  ContactZip: string
}

interface PartyFields {
  Type: 'individual' | 'married' | 'organization'
  OrganizationName: string
  Primary: PersonFields
  Spouse: PersonFields
}

interface OrderRequestFormInputs {
  OrderType: string
  ClosingDate: string
  RouteToId: string
  AgentRole: string
  AgentFirstName: string
  AgentLastName: string
  AgentEmail: string
  AgentPhone: string
  AgentFax: string
  AgentCompanyName: string
  AgentCompanyAddress1: string
  AgentCompanyAddress2: string
  AgentCompanyCity: string
  AgentCompanyState: string
  AgentCompanyZip: string
  PropertyAddress1: string
  PropertyAddress2: string
  PropertyCity: string
  PropertyState: string
  PropertyZip: string
  PropertyLegalDescription: string
  PropertyTaxId: string
  PropertyType: string
  Parties: PartyFields[]
  LenderName: string
  LoanAmount: string
  LoanNumber: string
  FinancingType: string
  LenderFirstName: string
  LenderLastName: string
  LenderEmail: string
  LenderPhone: string
  Comments: string
}

const defaultPerson: PersonFields = {
  FirstName: '',
  MiddleName: '',
  LastName: '',
  Email: '',
  Phone: '',
  ContactSameAsProperty: false,
  ContactAddress1: '',
  ContactAddress2: '',
  ContactCity: '',
  ContactState: '',
  ContactZip: '',
}

const defaultParty: PartyFields = {
  Type: 'individual',
  OrganizationName: '',
  Primary: { ...defaultPerson },
  Spouse: { ...defaultPerson },
}

function SectionHeader({ title }: { title: string }) {
  return (
    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1, mb: 2 }}>
      {title}
    </Typography>
  )
}

function StateSelect({
  name,
  control,
  required,
}: {
  name: any
  control: Control<OrderRequestFormInputs>
  required?: boolean
}) {
  return (
    <Controller
      name={name}
      control={control}
      rules={required ? { required: 'State is required' } : undefined}
      render={({ field, fieldState }) => (
        <FormControl fullWidth margin="normal" required={required} error={!!fieldState.error}>
          <InputLabel>State</InputLabel>
          <Select {...field} label="State">
            <MenuItem value=""><em>Select a State</em></MenuItem>
            {US_STATES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
          {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  )
}

function PersonFieldGroup({
  prefix,
  control,
}: {
  prefix: string
  control: Control<OrderRequestFormInputs>
}) {
  const contactSameAsProperty = useWatch({ control, name: `${prefix}.ContactSameAsProperty` as any })
  const [watchAddr1, watchAddr2, watchCity, watchState, watchZip] = useWatch({
    control,
    name: [
      `${prefix}.ContactAddress1` as any,
      `${prefix}.ContactAddress2` as any,
      `${prefix}.ContactCity` as any,
      `${prefix}.ContactState` as any,
      `${prefix}.ContactZip` as any,
    ],
  })
  const anyContactAddressFilled = !contactSameAsProperty && !!(watchAddr1 || watchAddr2 || watchCity || watchState || watchZip)

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Controller
            name={`${prefix}.FirstName` as any}
            control={control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState }) => (
              <TextField {...field} label="First Name" fullWidth margin="normal" required error={!!fieldState.error} helperText={fieldState.error?.message} />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Controller
            name={`${prefix}.MiddleName` as any}
            control={control}
            render={({ field }) => <TextField {...field} label="Middle Name" fullWidth margin="normal" />}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Controller
            name={`${prefix}.LastName` as any}
            control={control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState }) => (
              <TextField {...field} label="Last Name" fullWidth margin="normal" required error={!!fieldState.error} helperText={fieldState.error?.message} />
            )}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Controller
            name={`${prefix}.Email` as any}
            control={control}
            rules={{ pattern: EMAIL_PATTERN }}
            render={({ field, fieldState }) => (
              <TextField {...field} label="Email" type="email" fullWidth margin="normal" error={!!fieldState.error} helperText={fieldState.error?.message} />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name={`${prefix}.Phone` as any}
            control={control}
            rules={{ pattern: PHONE_PATTERN }}
            render={({ field, fieldState }) => (
              <TextField {...field} label="Phone" fullWidth margin="normal" error={!!fieldState.error} helperText={fieldState.error?.message} />
            )}
          />
        </Grid>
      </Grid>

      <Controller
        name={`${prefix}.ContactSameAsProperty` as any}
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={<Checkbox checked={field.value} onChange={field.onChange} />}
            label="Contact Address same as Property Address"
          />
        )}
      />

      {!contactSameAsProperty && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">Contact Address</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                name={`${prefix}.ContactAddress1` as any}
                control={control}
                rules={{ validate: (v) => !anyContactAddressFilled || !!v || 'Required' }}
                render={({ field, fieldState }) => <TextField {...field} label="Address 1" fullWidth margin="normal" required={anyContactAddressFilled} error={!!fieldState.error} helperText={fieldState.error?.message} />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name={`${prefix}.ContactAddress2` as any}
                control={control}
                render={({ field }) => <TextField {...field} label="Address 2" fullWidth margin="normal" />}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Controller
                name={`${prefix}.ContactCity` as any}
                control={control}
                rules={{ validate: (v) => !anyContactAddressFilled || !!v || 'Required' }}
                render={({ field, fieldState }) => <TextField {...field} label="City" fullWidth margin="normal" required={anyContactAddressFilled} error={!!fieldState.error} helperText={fieldState.error?.message} />}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StateSelect name={`${prefix}.ContactState`} control={control} required={anyContactAddressFilled} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name={`${prefix}.ContactZip` as any}
                control={control}
                rules={{ validate: (v) => !anyContactAddressFilled || !!v || 'Required' }}
                render={({ field, fieldState }) => <TextField {...field} label="Zip" fullWidth margin="normal" required={anyContactAddressFilled} error={!!fieldState.error} helperText={fieldState.error?.message} />}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  )
}

function PartySection({
  index,
  control,
  onRemove,
}: {
  index: number
  control: Control<OrderRequestFormInputs>
  onRemove?: () => void
}) {
  const partyType = useWatch({ control, name: `Parties.${index}.Type` })
  const isMarried = partyType === 'married'
  const isOrganization = partyType === 'organization'

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">Borrower {index + 1}</Typography>
        {onRemove && (
          <Button size="small" color="error" onClick={onRemove}>Remove</Button>
        )}
      </Box>

      <Controller
        name={`Parties.${index}.Type`}
        control={control}
        render={({ field }) => (
          <FormControl component="fieldset" sx={{ mb: 1 }}>
            <RadioGroup row {...field}>
              <FormControlLabel value="individual" control={<Radio size="small" />} label="Individual" />
              <FormControlLabel value="married" control={<Radio size="small" />} label="Married" />
              <FormControlLabel value="organization" control={<Radio size="small" />} label="Organization" />
            </RadioGroup>
          </FormControl>
        )}
      />

      {isOrganization && (
        <Controller
          name={`Parties.${index}.OrganizationName`}
          control={control}
          rules={{ required: 'Organization name is required' }}
          render={({ field, fieldState }) => (
            <TextField {...field} label="Organization Name" fullWidth margin="normal" required error={!!fieldState.error} helperText={fieldState.error?.message} />
          )}
        />
      )}

      {isMarried && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Primary Borrower
        </Typography>
      )}
      {isOrganization && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Contact Person
        </Typography>
      )}
      <PersonFieldGroup prefix={`Parties.${index}.Primary`} control={control} />

      {isMarried && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Spouse
          </Typography>
          <PersonFieldGroup prefix={`Parties.${index}.Spouse`} control={control} />
        </>
      )}
    </Box>
  )
}

export function OrderRequestForm({ onSuccess, sendToOptions = [] }: { onSuccess: (RequestId: string) => void; sendToOptions?: OrderRequestSendToOption[] }) {
  const { user } = useAuth0()

  const { control, getValues, handleSubmit, setValue } = useForm<OrderRequestFormInputs>({
    mode: 'onTouched',
    defaultValues: {
      OrderType: '',
      ClosingDate: '',
      RouteToId: '',
      AgentRole: '',
      AgentFirstName: '',
      AgentLastName: '',
      AgentEmail: '',
      AgentPhone: '',
      AgentFax: '',
      AgentCompanyName: '',
      AgentCompanyAddress1: '',
      AgentCompanyAddress2: '',
      AgentCompanyCity: '',
      AgentCompanyState: '',
      AgentCompanyZip: '',
      PropertyAddress1: '',
      PropertyAddress2: '',
      PropertyCity: '',
      PropertyState: '',
      PropertyZip: '',
      PropertyLegalDescription: '',
      PropertyTaxId: '',
      PropertyType: '',
      Parties: [{ ...defaultParty }],
      LenderName: '',
      LoanAmount: '',
      LoanNumber: '',
      FinancingType: '',
      LenderFirstName: '',
      LenderLastName: '',
      LenderEmail: '',
      LenderPhone: '',
      Comments: '',
    },
  })

  const { fields, append, remove } = useFieldArray({ control, name: 'Parties' })

  useEffect(() => {
    if (user?.email) setValue('AgentEmail', user.email)
  }, [user?.email, setValue])

  useEffect(() => {
    if (!sendToOptions.length || sendToOptions.length > 1) return

    const currentRouteToId = getValues('RouteToId')
    const hasValidSelection = !!currentRouteToId && sendToOptions.some((option) => option.id === currentRouteToId)

    if (!hasValidSelection) {
      setValue('RouteToId', sendToOptions[0].id)
    }
  }, [getValues, sendToOptions, setValue])

  const [documents, setDocuments] = useState<OrderRequestDocument[]>([])
  const RequestId = useMemo(() => uuidv4(), [])
  const [uploadingCount, setUploadingCount] = useState(0)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const uploadUrlMutation = useRequestOrderUploadUrl()
  const submitMutation = useSubmitOrderRequest()

  async function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    if (!e.target.files?.length) return
    const files = Array.from(e.target.files)
    e.target.value = ''
    setUploadError(null)
    setUploadingCount((c) => c + files.length)

    for (const file of files) {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        setUploadError(`File type not allowed: ${file.name}`)
        setUploadingCount((c) => c - 1)
        continue
      }
      try {
        const result = await uploadUrlMutation.mutateAsync({ FileExtension: ext, RequestId })
        const uploadResponse = await fetch(result.Url, {
          method: 'PUT',
          body: file,
          headers: {
            'x-amz-server-side-encryption': 'AES256',
            'x-amz-acl': 'bucket-owner-full-control',
          },
        })
        if (!uploadResponse.ok) {
          const text = await uploadResponse.text()
          throw new Error(`S3 upload failed: ${uploadResponse.status} - ${text}`)
        }
        setDocuments((prev) => [...prev, { DocumentFileName: result.DocumentFileName, OriginalFileName: file.name }])
      } catch {
        setUploadError(`Failed to upload ${file.name}. Please try again.`)
      } finally {
        setUploadingCount((c) => c - 1)
      }
    }
  }

  function removeDocument(index: number) {
    setDocuments((prev) => prev.filter((_, i) => i !== index))
  }

  const onSubmit: SubmitHandler<OrderRequestFormInputs> = async (data) => {
    const result = await submitMutation.mutateAsync({
      RequestId,
      OrderType: data.OrderType,
      ClosingDate: data.ClosingDate || undefined,
      RouteToId: data.RouteToId || undefined,
      RequestedBy: {
        Role: data.AgentRole,
        FirstName: data.AgentFirstName,
        LastName: data.AgentLastName,
        Email: data.AgentEmail,
        Phone: data.AgentPhone || undefined,
        Fax: data.AgentFax || undefined,
        CompanyName: data.AgentCompanyName,
        CompanyAddress1: data.AgentCompanyAddress1 || undefined,
        CompanyAddress2: data.AgentCompanyAddress2 || undefined,
        CompanyCity: data.AgentCompanyCity || undefined,
        CompanyState: data.AgentCompanyState || undefined,
        CompanyZip: data.AgentCompanyZip || undefined,
      },
      PropertyInfo: {
        Address1: data.PropertyAddress1,
        Address2: data.PropertyAddress2 || undefined,
        City: data.PropertyCity,
        State: data.PropertyState,
        Zip: data.PropertyZip,
        PropertyType: data.PropertyType || undefined,
        LegalDescription: data.PropertyLegalDescription || undefined,
        TaxId: data.PropertyTaxId || undefined,
      },
      Parties: data.Parties.map((party) => {
        const mapPerson = (p: PersonFields) => ({
          FirstName: p.FirstName,
          MiddleName: p.MiddleName || undefined,
          LastName: p.LastName,
          Email: p.Email || undefined,
          Phone: p.Phone || undefined,
          ContactAddress: p.ContactSameAsProperty || (!p.ContactAddress1 && !p.ContactAddress2 && !p.ContactCity && !p.ContactState && !p.ContactZip)
            ? undefined
            : { Address1: p.ContactAddress1, Address2: p.ContactAddress2 || undefined, City: p.ContactCity, State: p.ContactState, Zip: p.ContactZip },
        })
        return {
          Type: party.Type,
          ...(party.Type === 'organization' ? { OrganizationName: party.OrganizationName || undefined } : {}),
          Primary: mapPerson(party.Primary),
          ...(party.Type === 'married' ? { Spouse: mapPerson(party.Spouse) } : {}),
        }
      }),
      LenderInfo: {
        LenderName: data.LenderName || undefined,
        LoanAmount: data.LoanAmount || undefined,
        LoanNumber: data.LoanNumber || undefined,
        FinancingType: data.FinancingType || undefined,
        LenderFirstName: data.LenderFirstName || undefined,
        LenderLastName: data.LenderLastName || undefined,
        LenderEmail: data.LenderEmail || undefined,
        LenderPhone: data.LenderPhone || undefined,
      },
      Comments: data.Comments || undefined,
      Documents: documents,
    })
    onSuccess(result.RequestId)

  }

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>

      {/* Transaction Information */}
      <Box sx={{ p: 2 }}>
        <SectionHeader title="Transaction Information" />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="OrderType"
              control={control}
              rules={{ required: 'Type of Order is required' }}
              render={({ field, fieldState }) => (
                <FormControl fullWidth margin="normal" required error={!!fieldState.error}>
                  <InputLabel>Type of Order</InputLabel>
                  <Select {...field} label="Type of Order">
                    {ORDER_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                  {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="ClosingDate"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Closing Date" type="date" fullWidth margin="normal" InputLabelProps={{ shrink: true }} />
              )}
            />
          </Grid>
        </Grid>

        {sendToOptions.length > 0 && (
          <Controller
            name="RouteToId"
            control={control}
            rules={{ required: 'Please select a routing option' }}
            render={({ field, fieldState }) => (
              <FormControl fullWidth margin="normal" required error={!!fieldState.error} sx={{ maxWidth: 360 }}>
                <InputLabel>Route To</InputLabel>
                <Select {...field} label="Route To">
                  {sendToOptions.map((opt) => <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>)}
                </Select>
                {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
          Supporting Documents (Optional) — PDF, JPG, PNG, GIF
        </Typography>
        {uploadError && (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>{uploadError}</Typography>
        )}
        {documents.length > 0 && (
          <List dense>
            {documents.map((doc, idx) => (
              <ListItem
                key={idx}
                secondaryAction={
                  <IconButton edge="end" onClick={() => removeDocument(idx)}>
                    <CloseRounded />
                  </IconButton>
                }
              >
                <ListItemText primary={doc.OriginalFileName} />
              </ListItem>
            ))}
          </List>
        )}
        <Button component="label" variant="outlined" disabled={uploadingCount > 0}>
          {uploadingCount > 0
            ? <><CircularProgress size={16} sx={{ mr: 1 }} />Uploading...</>
            : 'Add Document'}
          <input type="file" accept={ACCEPT} hidden multiple onChange={handleFileChange} />
        </Button>
      </Box>

      <Divider />

      {/* Requested By */}
      <Box sx={{ p: 2 }}>
        <SectionHeader title="Requested By" />
        <Controller
          name="AgentRole"
          control={control}
          rules={{ required: 'Your Role is required' }}
          render={({ field, fieldState }) => (
            <FormControl margin="normal" required error={!!fieldState.error} sx={{ minWidth: 240 }}>
              <InputLabel>Your Role</InputLabel>
              <Select {...field} label="Your Role">
                {AGENT_ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
              {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
            </FormControl>
          )}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller name="AgentFirstName" control={control} rules={{ required: 'Required' }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="First Name" fullWidth margin="normal" required error={!!fieldState.error} helperText={fieldState.error?.message} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller name="AgentLastName" control={control} rules={{ required: 'Required' }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="Last Name" fullWidth margin="normal" required error={!!fieldState.error} helperText={fieldState.error?.message} />
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller name="AgentEmail" control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  fullWidth
                  margin="normal"
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                    tabIndex: -1,
                  }}
                  inputProps={{ tabIndex: -1 }}
                  variant="filled"
                  sx={{
                    '& .MuiFilledInput-root': {
                      backgroundColor: 'action.hover',
                      cursor: 'default',
                      '&:hover': { backgroundColor: 'action.hover' },
                      '&.Mui-focused': { backgroundColor: 'action.hover' },
                      '&::after': { display: 'none' },
                      '&::before': { display: 'none' },
                    },
                    '& .MuiFilledInput-input': { cursor: 'default' },
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller name="AgentPhone" control={control} rules={{ pattern: PHONE_PATTERN }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="Phone" fullWidth margin="normal" error={!!fieldState.error} helperText={fieldState.error?.message} />
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller name="AgentFax" control={control}
              render={({ field }) => <TextField {...field} label="Fax" fullWidth margin="normal" />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller name="AgentCompanyName" control={control} rules={{ required: 'Required' }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="Company Name" fullWidth margin="normal" required error={!!fieldState.error} helperText={fieldState.error?.message} />
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller name="AgentCompanyAddress1" control={control}
              render={({ field }) => <TextField {...field} label="Company Address 1" fullWidth margin="normal" />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller name="AgentCompanyAddress2" control={control}
              render={({ field }) => <TextField {...field} label="Company Address 2" fullWidth margin="normal" />}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Controller name="AgentCompanyCity" control={control}
              render={({ field }) => <TextField {...field} label="Company City" fullWidth margin="normal" />}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StateSelect name="AgentCompanyState" control={control} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="AgentCompanyZip" control={control}
              render={({ field }) => <TextField {...field} label="Company Zip Code" fullWidth margin="normal" />}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider />

      {/* Property Information */}
      <Box sx={{ p: 2 }}>
        <SectionHeader title="Property Information" />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller name="PropertyAddress1" control={control} rules={{ required: 'Required' }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="Address 1" fullWidth margin="normal" required error={!!fieldState.error} helperText={fieldState.error?.message} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller name="PropertyAddress2" control={control}
              render={({ field }) => <TextField {...field} label="Address 2" fullWidth margin="normal" />}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Controller name="PropertyCity" control={control} rules={{ required: 'Required' }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="City" fullWidth margin="normal" required error={!!fieldState.error} helperText={fieldState.error?.message} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StateSelect name="PropertyState" control={control} required />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller name="PropertyZip" control={control} rules={{ required: 'Required' }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="Zip" fullWidth margin="normal" required error={!!fieldState.error} helperText={fieldState.error?.message} />
              )}
            />
          </Grid>
        </Grid>
        <Controller name="PropertyLegalDescription" control={control}
          render={({ field }) => (
            <TextField {...field} label="Brief Legal Description" fullWidth margin="normal" multiline rows={2} />
          )}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller name="PropertyTaxId" control={control}
              render={({ field }) => <TextField {...field} label="Tax ID" fullWidth margin="normal" />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller name="PropertyType" control={control}
              rules={{ required: 'Property type is required' }}
              render={({ field, fieldState }) => (
                <FormControl fullWidth margin="normal" required error={!!fieldState.error}>
                  <InputLabel>Property Type</InputLabel>
                  <Select {...field} label="Property Type">
                    {PROPERTY_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                  {fieldState.error && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider />

      {/* Borrower Information */}
      <Box sx={{ p: 2 }}>
        <SectionHeader title="Borrower Information" />
        {fields.map((field, idx) => (
          <PartySection
            key={field.id}
            index={idx}
            control={control}
            onRemove={fields.length > 1 ? () => remove(idx) : undefined}
          />
        ))}
        <Button
          startIcon={<AddCircleOutline />}
          onClick={() => append({ ...defaultParty })}
          variant="outlined"
          size="small"
        >
          Add Borrower
        </Button>
      </Box>

      <Divider />

      {/* Lender Information */}
      <Box sx={{ p: 2 }}>
        <SectionHeader title="Lender Information" />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller name="LenderName" control={control}
              render={({ field }) => <TextField {...field} label="New Lender Name" fullWidth margin="normal" />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller name="LoanAmount" control={control}
              render={({ field }) => (
                <TextField {...field} label="Loan Amount" fullWidth margin="normal"
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller name="LoanNumber" control={control}
              render={({ field }) => <TextField {...field} label="Loan Number" fullWidth margin="normal" />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller name="FinancingType" control={control}
              render={({ field }) => (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Financing Type</InputLabel>
                  <Select {...field} label="Financing Type">
                    <MenuItem value=""><em>Select Type</em></MenuItem>
                    {FINANCING_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller name="LenderFirstName" control={control}
              render={({ field }) => <TextField {...field} label="Lender First Name" fullWidth margin="normal" />}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller name="LenderLastName" control={control}
              render={({ field }) => <TextField {...field} label="Lender Last Name" fullWidth margin="normal" />}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller name="LenderEmail" control={control} rules={{ pattern: EMAIL_PATTERN }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="Lender Email" type="email" fullWidth margin="normal" error={!!fieldState.error} helperText={fieldState.error?.message} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller name="LenderPhone" control={control} rules={{ pattern: PHONE_PATTERN }}
              render={({ field, fieldState }) => (
                <TextField {...field} label="Lender Phone Number" fullWidth margin="normal" error={!!fieldState.error} helperText={fieldState.error?.message} />
              )}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider />

      {/* Comments */}
      <Box sx={{ p: 2 }}>
        <SectionHeader title="Comments / Additional Information" />
        <Controller name="Comments" control={control}
          render={({ field }) => (
            <TextField {...field} label="Comments / Additional Information" fullWidth margin="normal" multiline rows={4} />
          )}
        />
      </Box>

      <Grid container justifyContent="flex-end" sx={{ pr: 2, pb: 2 }}>
        {submitMutation.isError && (
          <Typography color="error" sx={{ mr: 2, alignSelf: 'center' }}>
            There was an error submitting your request. Please try again.
          </Typography>
        )}
        <Button
          type="submit"
          variant="contained"
          disabled={submitMutation.isLoading || uploadingCount > 0}
        >
          {submitMutation.isLoading ? 'Submitting...' : 'Submit Request'}
        </Button>
      </Grid>
    </form>
  )
}
